/**
 * Wave 312 · Prumo brand mark.
 *
 * The plumb line: a vertical thread with a weighted bob at the bottom.
 * Used in pt-BR construction to verify if a wall is truly vertical;
 * "ficar no prumo" = stay aligned. Domain-perfect for an operational
 * copilot that keeps decisions in line with rules.
 *
 * Visual contract — keep distinct from Housy:
 *   - Housy: pixel face, balloon shape, warm accent, gentle bounce.
 *   - Prumo: 1.5px stroke vertical line + filled bob, neutral ink,
 *     no idle motion.
 *
 * The mark scales by `size` (px). Color is currentColor so any parent
 * text-color class drives both the line and the bob. Active state
 * (`active` prop true) flips to brand-500 — used by the top-bar pill
 * when there are open proposals to flag attention without yelling.
 */

interface Props {
  size?: number;
  active?: boolean;
  className?: string;
}

export function PrumoMark({ size = 16, active = false, className }: Props) {
  // viewBox 8x16 — taller than wide on purpose; the plumb line is
  // vertical by definition. Stroke kept on integer pixel grid (cx=4,
  // line x=4) so it stays crisp at small sizes.
  return (
    <svg
      width={size / 2}
      height={size}
      viewBox="0 0 8 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      role="presentation"
      style={{ color: active ? 'rgb(var(--brand-500))' : 'currentColor' }}
    >
      {/* String — anchored at top, runs to where the bob hangs */}
      <line
        x1="4"
        y1="1"
        x2="4"
        y2="11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      {/* Anchor pin at the top — 1.5px diameter to balance the bob */}
      <circle cx="4" cy="1" r="0.75" fill="currentColor" />
      {/* The bob — heavy, filled, slightly larger so the line reads
          as supporting it */}
      <path
        d="M 4 11 L 6 13 L 4 15 L 2 13 Z"
        fill="currentColor"
      />
    </svg>
  );
}
